/* eslint-disable @typescript-eslint/no-empty-interface */
/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  Class,
  ImmutableClass,
  Immutable,
  Mutable,
  types,
} from "../helpers/Util";
import {
  WhereClause,
  insertIntoTable,
  upsertRow,
  queryTable,
  deleteTableRow,
  postgrest_operators,
} from "../helpers/HttpHelper";
import store from "../store/Store";
import { tableSliceActions } from "../store/slices/TableSlice";
import { guidGenerator } from "../helpers/DataUpdateHelper";
import { List } from "immutable";

const updateRedux = (databaseTable: DatabaseTable) => {
  const state = store.getState();

  const table = databaseTable.constructor as typeof DatabaseTable;
  const pk = table.primaryKey;
  const tableID = databaseTable[pk as keyof DatabaseTable];
  if (typeof tableID === "undefined") {
    console.error("Data has no primary key, unable to save to redux");
    return;
  }

  const rows = state.tables.tables.get(table);
  const row = rows?.get(tableID);
  if (typeof rows === "undefined" || typeof row === "undefined") {
    store.dispatch(
      tableSliceActions.insertTableRow({
        table,
        id: tableID,
        data: databaseTable,
      }),
    );
  } else {
    store.dispatch(
      tableSliceActions.updateTableRow({
        table,
        id: tableID,
        data: databaseTable,
      }),
    );
  }
};
const deleteFromRedux = (table: typeof DatabaseTable, pk: any) => {
  store.dispatch(tableSliceActions.deleteTableRow({ table, pk }));
};

// Converts from Typescript models to postgres tables
// Typescript models are PascalCase
// postgres tables are snake_case
export const convertTableName = (tableName: string): string => {
  if (tableName.endsWith("raw")) {
    tableName = tableName.slice(0, -3);
  }
  return tableName
    .replace(/\.?([A-Z]+)/g, "_$1")
    .toLowerCase()
    .replace(/^_/, "");
};

export const convertToCamelCase = (tableName: string): string => {
  return tableName
    .toLowerCase()
    .replace(/(_\w)/g, match => match[1].toUpperCase());
};

export const convertToNormalWords = (tableName: string): string => {
  return tableName
    .toLowerCase()
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

export class DatabaseTable extends ImmutableClass {
  static readonly serializableFields: Map<string, types[]> = new Map();
  static readonly primaryKey: string = "id";
  static readonly tableName: string = "";

  private static readonly initializedReceipt = new (class {
    private property = "";
  })();

  protected constructor() {
    super();
  }

  // Can be overridden to initialize fields that aren't serialized, but must return super.initialize()
  protected initialize() {
    updateRedux(this);
    return DatabaseTable.initializedReceipt;
  }

  // This should never be overridden, instead override the initialize function
  public static createInstance<T extends ImmutableClass>(
    this: Class<T>,
    data: Partial<T>,
  ): Immutable<T> {
    const instance = super.createInstance(data) as T;
    (instance as unknown as DatabaseTable).initialize();
    return instance;
  }

  public getPK() {
    const pk = (this.constructor as typeof DatabaseTable).primaryKey;
    const pkVal = this[pk as keyof DatabaseTable];
    return pkVal;
  }

  public static async queryTable<T extends DatabaseTable>(
    this: Class<T>,
    clauses: WhereClause[] = [],
  ): Promise<Immutable<T>[]> {
    const tableName: string = convertTableName(
      (this.prototype.constructor as typeof DatabaseTable).tableName,
    );
    const response = await queryTable(tableName, clauses);
    const responseJSON = await response.json();
    const rowModels = [];
    for (const rowJSON of responseJSON) {
      rowModels.push(this.prototype.constructor.fromJSON(rowJSON));
    }
    return rowModels;
  }

  public static async fetchRow<T extends DatabaseTable>(
    this: Class<T>,
    pk: any,
  ): Promise<Immutable<T>> {
    const tableClass = this as unknown as typeof DatabaseTable;
    const rowModels = await tableClass.queryTable([
      {
        operator: postgrest_operators.eq,
        column: tableClass.primaryKey,
        value: String(pk),
      },
    ]);
    if (rowModels.length !== 1) {
      throw new Error("Query to table did not return a single row");
    }
    return rowModels[0] as Immutable<T>;
  }

  // This should never be overridden, instead override the onUpdate function
  public update<K extends keyof this>(key: K, value: this[K]): Immutable<this> {
    const copy = super.update(key, value) as typeof this;
    updateRedux(copy);
    copy.onUpdate(key, value as any);
    return copy;
  }

  // Called after an update is made to database table
  // Can be overridden to edit generated fields in database table class
  // Could also be used to update other tables that depend on data from this table
  protected onUpdate<K extends keyof this>(key: K, value: this[K]): void {
    return;
  }

  // Called before we save the table
  // Can be overridden by any table to transform data before saving
  protected beforeSave(): this {
    return this;
  }

  public async save(): Promise<this> {
    const table_name = convertTableName(
      (this.constructor as typeof DatabaseTable).tableName,
    );
    // We only want to save fields that are serialized
    // If a field isn't serialized, it's generated specifically for UI and isn't in the DB
    const tableFields = (this.constructor as typeof DatabaseTable)
      .serializableFields;
    const pk = (this.constructor as typeof DatabaseTable).primaryKey;

    const data = { ...this.beforeSave() } as any;
    const oldPKVal = this[pk as keyof DatabaseTable];
    for (const [column, value] of Object.entries(data)) {
      const possibleTypes = tableFields.get(column);

      // If it isn't in tableFields, it shouldn't be saved to db at all
      if (typeof possibleTypes === "undefined") {
        delete data[column];
        continue;
      }

      // If null is possible for fields, make sure dummy values are converted to null
      if (possibleTypes.includes(types.null)) {
        if (typeof value === "string" && value.trim() === "") {
          data[column] = null;
        } else if (typeof value === "number" && value === -1) {
          data[column] = null;
        }
      }
    }
    // Database will manage modified values, so don't send our value
    delete data.modified;

    let response;
    // Negative id means it was generated in UI, needs to be inserted
    const insert = typeof data.id === "number" && data.id < 0;
    if (insert) {
      response = await insertIntoTable(table_name, data);
    } else {
      response = await upsertRow(table_name, data);
    }
    if (!response.ok) {
      throw new Error("Unable to save table");
    }

    const contentType = response.headers.get("content-type");

    let newInstance: typeof this | null = null;
    if (contentType && contentType.indexOf("application/json") !== -1) {
      // Return new instance reinitialized from database with updated data
      const responseJSON = await response.json();
      newInstance = Object.getPrototypeOf(this).constructor.fromJSON(
        responseJSON[0],
      );
    } else {
      if (insert) {
        throw new Error(
          "Attempted insert to database, but did not receive inserted record back",
        );
      }
    }

    if (newInstance !== null) {
      const newPKVal = newInstance[pk as keyof DatabaseTable];
      if (oldPKVal !== newPKVal) {
        // Need to delete old instance from redux
        deleteFromRedux(this.constructor as typeof DatabaseTable, oldPKVal);
        newInstance.afterSave(this);
        return newInstance;
      }
    }
    await this.afterSave();
    return this;
  }

  // Called after we save a table
  // Useful if db updates certain columns, such as ID, and other tables rely on those columns
  protected async afterSave(oldRecord?: DatabaseTable) {
    return;
  }

  public async delete(commit?: boolean): Promise<void> {
    if (typeof commit === "undefined") {
      commit = true;
    }
    const pk = (this.constructor as typeof DatabaseTable).primaryKey;
    const pkVal = this[pk as keyof DatabaseTable];
    const table_name = convertTableName(
      (this.constructor as typeof DatabaseTable).tableName,
    );

    if (commit) {
      deleteTableRow(table_name, pkVal.toString(), pk);
    }
    deleteFromRedux(this.constructor as typeof DatabaseTable, pkVal);
    this.onDelete();
  }

  // Called after we delete the database table row, can be overridden to cascade deletion to other tables
  protected onDelete(): void {
    return;
  }
}

export const returnID = (id: number): number => {
  if (id < 0) {
    return -1 * guidGenerator();
  } else {
    return id;
  }
};

export const getAllRowsForTable = <T extends DatabaseTable>(
  table: Class<T>,
): List<Immutable<T>> => {
  const tableMap = store
    .getState()
    .tables.tables.get(table as unknown as typeof DatabaseTable);
  if (typeof tableMap === "undefined") {
    return List() as List<T>;
  }
  return tableMap.toList() as List<T>;
};

export const getTableRow = <T extends DatabaseTable>(
  table: Class<T>,
  pk: number,
): Immutable<T> | undefined =>
  store
    .getState()
    .tables.tables.get(table as unknown as typeof DatabaseTable)
    ?.get(pk) as undefined | Immutable<T>;
